import { cloneDeep } from "es-toolkit/compat";
import { Grid, IGridItem } from "./Grid";

class ResizingModule {
    constructor(private grid: Grid) {}

    private checkAdjacentEmptySpaces(
        row: IGridItem[],
        itemCol: number,
        itemColSpan: number,
        newColSpan: number,
        direction: "left" | "right" = "right"
    ): { availableSpaces: number; colStart: number } {
        const virtualRow = Grid.virtualizeRow(row);
        const expansionNeeded = newColSpan - itemColSpan;

        if (direction === "left") {
            let availableLeftSpaces = 0;
            let colStart = itemCol;
            for (let i = itemCol - 1; i >= 0; i--) {
                const item = virtualRow[i];
                if (item && item.empty) {
                    availableLeftSpaces++;
                    colStart = i;
                } else {
                    break;
                }
            }
            return { availableSpaces: availableLeftSpaces, colStart };
        }

        // * Check spaces to the right of the item
        let availableRightSpaces = 0;
        let colStart = itemCol;
        for (let i = itemCol + itemColSpan; i < virtualRow.length && i < itemCol + newColSpan; i++) {
            const item = virtualRow[i];
            if (item && item.empty) {
                availableRightSpaces++;
                colStart = i;
            } else {
                break; // * Hit a non-empty item
            }
        }

        return { availableSpaces: availableRightSpaces, colStart };
    }

    resize(item: IGridItem, size: { colSpan: number }) {
        const itemId = item.id;
        const itemRow = this.grid.getItemCoordinates(itemId, false);
        if (!itemRow) return;

        const pastItem = this.grid.getItem(item.id, false);
        if (!pastItem) return;

        const { consecutiveSpace: currentRowExtraSpace, colStart } = this.grid.getRowConsecutiveSpace(this.grid.grid[itemRow?.row ?? 0]);

        const increaseSize = size.colSpan > (pastItem.colSpan ?? 0);
        const decreaseSize = size.colSpan < (pastItem.colSpan ?? 0);
        const sizeDiff = Math.abs(size.colSpan - (pastItem.colSpan ?? 0));
        const thereIsEnoughSpaceInCurrentRow = currentRowExtraSpace >= size.colSpan - (pastItem.colSpan ?? 0);

        // * Check if there are enough adjacent empty spaces specifically next to this item
        const { availableSpaces: rightAvailableSpaces, colStart: rightColStart } = this.checkAdjacentEmptySpaces(
            this.grid.grid[itemRow?.row ?? 0],
            itemRow.col,
            pastItem.colSpan ?? 1,
            size.colSpan,
            "right"
        );
        const { availableSpaces: leftAvailableSpaces, colStart: leftColStart } = this.checkAdjacentEmptySpaces(
            this.grid.grid[itemRow?.row ?? 0],
            itemRow.col,
            pastItem.colSpan ?? 1,
            size.colSpan,
            "left"
        );
        const hasAdjacentSpace = rightAvailableSpaces >= sizeDiff;
        const hasLeftAdjacentSpace = leftAvailableSpaces >= sizeDiff;

        // debugger;
        const _grid = cloneDeep(this.grid.grid);

        // ******* HAPPY PATHS *******
        // * If there is enough adjacent space and it is an increase
        if (increaseSize && hasAdjacentSpace) {
            console.log("CASE 1", { itemRow, size });
            const row = _grid[itemRow?.row ?? 0];
            row[itemRow?.col ?? 0].colSpan = size.colSpan;
            row[itemRow.col ?? 0].colStart = rightColStart;
            // * Remove only the empty items immediately to the right of the current item
            const startRemoveIndex = itemRow?.col + 1;
            row.splice(startRemoveIndex, sizeDiff);

            this.grid.setGrid(_grid);
            return;
        }
        // * If there is enough adjacent space and it is an increase
        if (increaseSize && hasLeftAdjacentSpace) {
            console.log("CASE 2", { itemRow, size, leftAvailableSpaces, leftColStart });
            const row = _grid[itemRow?.row ?? 0];
            row[itemRow?.col ?? 0].colSpan = size.colSpan;
            row[itemRow.col ?? 0].colStart = leftColStart;
            // * Remove only the empty items immediately to the left of the current item
            const startRemoveIndex = itemRow?.col - 1;
            row.splice(leftColStart, sizeDiff);

            this.grid.setGrid(_grid);
            return;
        }

        // * it is a decrease - there is enough space in the current row
        if (decreaseSize && thereIsEnoughSpaceInCurrentRow) {
            console.log("CASE 3", { itemRow, size });
            const row = _grid[itemRow?.row ?? 0];

            row[itemRow?.col ?? 0].colSpan = size.colSpan;
            row[itemRow.col ?? 0].colStart = itemRow.col;

            const emptyItems = new Array(sizeDiff).fill(0).map((_, index) => Grid.emptyItem(1, itemRow.col + 1 + index));
            row.splice(itemRow?.col + 1, 0, ...emptyItems);

            this.grid.setGrid(_grid);
            return;
        }

        // ****************************

        // * Increase + Not enough adjacent space - Combining resizing and dragging features
        if (increaseSize && !hasAdjacentSpace) {
            console.log("CASE 4", { itemRow, size });
            const newRow = this.grid.addRow(itemRow?.row ?? 0);
            this.grid.removeItem(this.grid.getItemCoordinates(item.id));
            this.grid.placeItem({ ...item, colSpan: size.colSpan }, { row: newRow, col: itemRow.col, colSpan: size.colSpan }, 0);
            return;
        }
    }
}

export { ResizingModule };
